import React from "react";
import { View, Font, Svg, Circle, Polyline, G, Path, Text, Line } from "@react-pdf/renderer";
import { useGauge } from "use-gauge";

Font.register({
  family: "Inter",
  fonts: [
    { src: `${process.env.PUBLIC_URL}/font/Inter-Regular.ttf` },
    { src: `${process.env.PUBLIC_URL}/font/Inter-Medium.ttf`, fontWeight: 500 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-SemiBold.ttf`, fontWeight: 600 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-Bold.ttf`, fontWeight: 700 },
  ],
});

Font.registerHyphenationCallback((word) => [word]);

const START_ANGLE = 45;
const END_ANGLE = 315;

const constrainValue = (value, min, max) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

export function ArcedDiff(props) {
  const { value01, color01, value02, color02, quinaryScale } = props;

  const isQuinary = quinaryScale === 1;

  const domainMin = isQuinary ? 1 : 0;
  const domainMax = isQuinary ? 5 : 100;

  const constrainedValue = constrainValue(Number(value01 ?? 0), domainMin, domainMax);

  const hasPrevValue = typeof value02 === "number" && !Number.isNaN(value02);
  const constrainedPrevValue = hasPrevValue ? constrainValue(Number(value02), domainMin, domainMax) : null;

  const gauge = useGauge({
    domain: [domainMin, domainMax],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: isQuinary ? 5 : 21,
    diameter: 300,
  });

  const needle = gauge.getNeedleProps({
    value: constrainedValue,
    baseRadius: 14,
    tipRadius: 1,
  });

  const prevNeedle =
    constrainedPrevValue !== null
      ? gauge.getNeedleProps({
          value: constrainedPrevValue,
          baseRadius: 14,
          tipRadius: 1,
        })
      : null;

  const quinaryAngle = (v) => {
    if (typeof gauge.valueToAngle === "function") return gauge.valueToAngle(v);
    const t = (v - domainMin) / (domainMax - domainMin);
    return START_ANGLE + t * (END_ANGLE - START_ANGLE);
  };

  return (
    <View style={{ padding: 0 }}>
      <Svg
        style={{
          width: "100%",
          overflow: "visible",
          padding: 0,
        }}
        {...gauge.getSVGProps()}
      >
        <G id="arcs" style={{ transform: "scale(0.7, 0.7)" }}>
          {!isQuinary ? (
            <>
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: START_ANGLE,
                  endAngle: END_ANGLE,
                })}
                fill="none"
                style={{ stroke: "#639754" }}
                strokeLinecap="round"
                strokeWidth={30}
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: START_ANGLE,
                  endAngle: 140,
                })}
                fill="none"
                style={{ stroke: "#e03c32" }}
                strokeLinecap="round"
                strokeWidth={30}
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: 140,
                  endAngle: 180,
                })}
                fill="none"
                style={{ stroke: "#ffd301" }}
                strokeWidth={30}
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: 180,
                  endAngle: 220,
                })}
                fill="none"
                style={{ stroke: "#7bb662" }}
                strokeWidth={30}
              />
            </>
          ) : (
            <>
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: START_ANGLE,
                  endAngle: END_ANGLE,
                })}
                fill="none"
                style={{ stroke: "#639754" }}
                strokeLinecap="round"
                strokeWidth={30}
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: quinaryAngle(1),
                  endAngle: quinaryAngle(2),
                })}
                fill="none"
                style={{ stroke: "#e03c32" }}
                strokeLinecap="round"
                strokeWidth={30}
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: quinaryAngle(2),
                  endAngle: quinaryAngle(3),
                })}
                fill="none"
                style={{ stroke: "#ffd301" }}
                strokeWidth={30}
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: quinaryAngle(3),
                  endAngle: quinaryAngle(4),
                })}
                fill="none"
                style={{ stroke: "#7bb662" }}
                strokeWidth={30}
              />
            </>
          )}
        </G>

        <G id="ticks" style={{ transform: "scale(0.7, 0.7)" }}>
          {gauge.ticks.map((angle) => {
            const asValue = gauge.angleToValue(angle);

            const showText = isQuinary
              ? asValue === 1 || asValue === 2 || asValue === 3 || asValue === 4 || asValue === 5
              : asValue === 35 || asValue === 50 || asValue === 65;

            return (
              <React.Fragment key={`tick-group-${angle}`}>
                <Line
                  strokeWidth={1}
                  stroke={color01}
                  strokeOpacity={0.7}
                  {...gauge.getTickProps({ angle, length: showText ? 12 : 6 })}
                />
                {showText && (
                  <Text {...gauge.getLabelProps({ angle, offset: -70 })} fill={color01}>
                    {asValue}
                  </Text>
                )}
              </React.Fragment>
            );
          })}
        </G>

        <G id="needle" style={{ transform: "scale(0.7, 0.7)" }}>
          {prevNeedle && (
            <G id="prev-needle">
              <Circle fill={color02} fillOpacity={0.3} {...prevNeedle.base} />
              <Circle fill={color02} fillOpacity={0.3} {...prevNeedle.tip} />
              <Polyline fill={color02} fillOpacity={0.3} points={prevNeedle.points} />
            </G>
          )}

          <Circle fill={color01} {...needle.base} />
          <Circle fill={color01} {...needle.tip} />
          <Polyline fill={color01} points={needle.points} />
        </G>
      </Svg>
    </View>
  );
}
